/**
 * 重连退避（§7.10）。
 *
 * ★ 这个文件里唯一重要的东西不是曲线，是**重置时机**。
 *   曲线再怎么调，用户能感知到的只有「从地铁里出来之后要盯着屏幕等多久」。
 *   漏掉任何一个重置时机，退避就会带着地铁里攒下的 attempt 数一路指数到 30s，
 *   网络明明已经好了，用户还要干等半分钟。五个必须归零的时机见 `BackoffResetReason`。
 *
 * 抖动是必须的：一个房间里 9 个人往往在同一秒断线（服务端重启 / Wi-Fi 掉线），
 * 不抖动的话 9 台手机会在同一毫秒重连，把刚起来的服务端再打一次。
 */
import { WS } from '@poker/shared'

/**
 * 必须把 attempt 归零的五个时机（§7.10）。
 * 每一条都对应一个真实场景，删任何一条都会退化成「网络好了还在等」：
 *  - `welcome`      —— 连上了，历史失败与当下无关
 *  - `online`       —— 系统告诉我们网络回来了
 *  - `visible`      —— 用户把 App 切回前台，他正在看着屏幕
 *  - `manualRetry`  —— 用户亲手点了「立即重试」
 *  - `pageshow`     —— bfcache 恢复，页面其实是「新」的
 */
export type BackoffResetReason = 'welcome' | 'online' | 'visible' | 'manualRetry' | 'pageshow'

export const BACKOFF_RESET_REASONS = [
  'welcome',
  'online',
  'visible',
  'manualRetry',
  'pageshow',
] as const satisfies readonly BackoffResetReason[]

/** 连续失败超过这么多次**且**持续超过 `GIVE_UP_AFTER_MS`，才停掉自动重连（§7.10）。 */
export const GIVE_UP_ATTEMPTS = 8
export const GIVE_UP_AFTER_MS = 60_000

/** 抖动区间 [0.7, 1.3]，与 §7.10 的公式一致。 */
export const JITTER_MIN = 0.7
export const JITTER_SPAN = 0.6

/**
 * 第 `attempt` 次重试的等待时长（attempt 从 0 起）。
 *
 * 指数部分额外用 `WS.BACKOFF_MAX_ATTEMPT` 夹一次：
 * `1.8 ** 40` 会算出 1e10 这种数，虽然随后被 `Math.min` 截到 30s，
 * 但中间值溢出成 Infinity 时 `Infinity * jitter` 就是 NaN，setTimeout(NaN) 会立刻回调 —— 变成死循环重连。
 */
export function backoffDelayMs(attempt: number, rand: () => number = Math.random): number {
  const exp = Math.min(Math.max(0, Math.trunc(attempt)), WS.BACKOFF_MAX_ATTEMPT)
  const base = Math.min(WS.BACKOFF_CAP_MS, WS.BACKOFF_BASE_MS * WS.BACKOFF_FACTOR ** exp)
  return Math.round(base * (JITTER_MIN + rand() * JITTER_SPAN))
}

export interface BackoffSnapshot {
  readonly attempt: number
  /** 第一次失败的时刻；成功后回到 null */
  readonly failingSinceMs: number | null
  /** true = UI 应从「重新连接中」切到「网络异常，点击重试」并停止自动重连 */
  readonly exhausted: boolean
}

export interface Backoff {
  /** 记一次失败并返回下次重试的等待时长 */
  fail(nowMs: number): number
  reset(reason: BackoffResetReason): void
  attempt(): number
  /** 连续失败 > 8 次且 > 60s 未成功 —— 停止自动重连（省电），交给用户点「立即重试」 */
  exhausted(nowMs: number): boolean
  snapshot(nowMs: number): BackoffSnapshot
  /** 最后一次 reset 的原因，调试与埋点用 */
  lastReset(): BackoffResetReason | null
}

export interface BackoffOptions {
  /** 注入随机源，测试里给确定值 */
  readonly rand?: () => number
  readonly giveUpAttempts?: number
  readonly giveUpAfterMs?: number
}

export function createBackoff(options: BackoffOptions = {}): Backoff {
  const rand = options.rand ?? Math.random
  const giveUpAttempts = options.giveUpAttempts ?? GIVE_UP_ATTEMPTS
  const giveUpAfterMs = options.giveUpAfterMs ?? GIVE_UP_AFTER_MS

  let attempt = 0
  let failingSince: number | null = null
  let lastReset: BackoffResetReason | null = null

  const exhausted = (nowMs: number): boolean =>
    attempt > giveUpAttempts && failingSince !== null && nowMs - failingSince > giveUpAfterMs

  return {
    fail(nowMs) {
      if (failingSince === null) failingSince = nowMs
      const delay = backoffDelayMs(attempt, rand)
      attempt += 1
      return delay
    },
    reset(reason) {
      attempt = 0
      failingSince = null
      lastReset = reason
    },
    attempt: () => attempt,
    exhausted,
    snapshot: (nowMs) => ({ attempt, failingSinceMs: failingSince, exhausted: exhausted(nowMs) }),
    lastReset: () => lastReset,
  }
}
